import { getArenaUrl } from '@/lib/arena'
import { resolvePublicContentLocale } from '../../functions/public-content-locale.js'
import {
  blogSources,
  type BlogContentLanguage,
} from '@/content/blogs'
import {
  type LanguageCode,
  getLegacyQueryLanguage,
  getPathLanguage,
  stripLanguagePrefix,
  withLanguagePath,
} from '@/lib/url-language-utils'

export interface ContentLinkContext {
  pathname: string
  search: string
  arenaOrigin?: string | null
  contentLanguage?: BlogContentLanguage
}

const INTERNAL_HOSTS = new Set([
  'mentiss.ai',
  'www.mentiss.ai',
  'werewolf.mentiss.ai',
  'b-app.mentiss.ai',
  'b-werewolf.mentiss.ai',
])

const ARENA_HOSTS = new Set([
  'werewolf.mentiss.ai',
  'b-werewolf.mentiss.ai',
])

const APEX_HOST_BY_ARENA_HOST: Record<string, string> = {
  'werewolf.mentiss.ai': 'mentiss.ai',
  'b-werewolf.mentiss.ai': 'b-app.mentiss.ai',
}

const ENGLISH_ONLY_RESEARCH_PATHS = new Set([
  '/career',
  '/legal/privacy',
  '/legal/terms',
  '/legal/cookies',
  '/ai-werewolf-benchmark',
  '/mafia-ai-benchmark',
  '/board-game-ai-benchmark',
  '/social-deduction-ai-benchmark',
])

const PUBLIC_PAGE_PATHS = new Set([
  '/',
  '/new',
  '/blogs',
  '/faq',
  '/contact-us',
  '/career',
  '/ai-werewolf-benchmark',
  '/mafia-ai-benchmark',
  '/board-game-ai-benchmark',
  '/social-deduction-ai-benchmark',
  '/play',
  '/play/rule',
  '/play/roles',
  '/games',
  '/leaderboard',
  '/lobby',
  '/lobby/rules',
  '/forum',
  '/forum/new',
  '/review',
  '/demo',
  '/grpo',
  '/signin',
  '/signup',
  '/forget-password',
  '/reset-password',
  '/dashboard',
  '/legal/privacy',
  '/legal/terms',
  '/legal/cookies',
])

const PUBLIC_PAGE_WITH_ID = /^\/(?:blogs|play|mobile|players|room|parallel|review|demo)\/[^/]+$/
const FORUM_THREAD_WITH_ID = /^\/forum\/thread\/[^/]+$/
const BLOG_ARTICLE_PATH = /^\/blogs\/([^/]+)\/?$/
const LEGACY_BLOG_ARTICLE_PATH = /^\/blogs\/(en|zh)\/([^/]+)\/?$/

function isPublicPagePath(pathname: string): boolean {
  const unprefixed = stripLanguagePrefix(pathname)
  const path = unprefixed.endsWith('/') && unprefixed !== '/'
    ? unprefixed.slice(0, -1)
    : unprefixed
  return PUBLIC_PAGE_PATHS.has(path)
    || PUBLIC_PAGE_WITH_ID.test(path)
    || FORUM_THREAD_WITH_ID.test(path)
}

export function currentContentLinkContext(): ContentLinkContext {
  if (typeof window === 'undefined')
    return { pathname: '/', search: '' }

  return {
    pathname: window.location.pathname,
    search: window.location.search,
    arenaOrigin: getArenaUrl(),
  }
}

export function localizeContentHref(
  href: string | undefined,
  context: ContentLinkContext,
): string | undefined {
  if (!href || href.startsWith('#') || href.startsWith('?'))
    return href

  const isRootRelative = href.startsWith('/') && !href.startsWith('//')
  const isAbsolute = /^https?:\/\//i.test(href)
  if (!isRootRelative && !isAbsolute)
    return href

  let url: URL
  try {
    url = new URL(href, 'https://mentiss.ai')
  }
  catch {
    return href
  }

  if (isAbsolute && !INTERNAL_HOSTS.has(url.hostname))
    return href

  const pathLanguage = getPathLanguage(context.pathname)
  const legacyLanguage = pathLanguage ? null : getLegacyQueryLanguage(context.search)
  const unprefixedPath = stripLanguagePrefix(url.pathname)
  const normalizedPath = unprefixedPath.endsWith('/') && unprefixedPath !== '/'
    ? unprefixedPath.slice(0, -1)
    : unprefixedPath
  const articleMatch = BLOG_ARTICLE_PATH.exec(unprefixedPath)
  const legacyArticleMatch = LEGACY_BLOG_ARTICLE_PATH.exec(unprefixedPath)
  const articleId = legacyArticleMatch?.[2] ?? articleMatch?.[1]
  const articleSource = blogSources.find(source => source.id === articleId)
  let changed = false

  if (articleSource) {
    const preferredLanguage = (legacyArticleMatch?.[1] as BlogContentLanguage | undefined)
      ?? getPathLanguage(url.pathname)
      ?? getLegacyQueryLanguage(url.search)
      ?? pathLanguage
      ?? legacyLanguage
      ?? context.contentLanguage
      ?? 'en'
    const articlePath = `/blogs/${articleSource.id}`
    const actualLanguage = resolvePublicContentLocale(
      articlePath,
      preferredLanguage,
      Object.keys(articleSource.locales) as LanguageCode[],
    ) ?? preferredLanguage
    const localizedPath = withLanguagePath(articlePath, actualLanguage)
    if (url.pathname !== localizedPath) {
      url.pathname = localizedPath
      changed = true
    }
    if (url.searchParams.has('lang')) {
      url.searchParams.delete('lang')
      changed = true
    }
    if (isAbsolute && ARENA_HOSTS.has(url.hostname)) {
      url.hostname = APEX_HOST_BY_ARENA_HOST[url.hostname]!
      changed = true
    }
    return changed
      ? isRootRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
      : href
  }

  if (normalizedPath === '/faq' || ENGLISH_ONLY_RESEARCH_PATHS.has(normalizedPath)) {
    const requestedLanguage = getPathLanguage(url.pathname)
      ?? getLegacyQueryLanguage(url.search)
      ?? context.contentLanguage
      ?? pathLanguage
      ?? legacyLanguage
      ?? 'en'
    const actualLanguage = resolvePublicContentLocale(
      normalizedPath,
      requestedLanguage,
    ) ?? requestedLanguage
    const localizedPath = withLanguagePath(normalizedPath, actualLanguage)
    if (url.pathname !== localizedPath) {
      url.pathname = localizedPath
      changed = true
    }
    if (url.searchParams.has('lang')) {
      url.searchParams.delete('lang')
      changed = true
    }
    if (isAbsolute && ARENA_HOSTS.has(url.hostname)) {
      url.hostname = APEX_HOST_BY_ARENA_HOST[url.hostname]!
      changed = true
    }
    return changed
      ? isRootRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
      : href
  }

  if (!isPublicPagePath(url.pathname))
    return href

  const destinationLanguage = getPathLanguage(url.pathname)
    ?? pathLanguage
    ?? legacyLanguage
    ?? getLegacyQueryLanguage(url.search)
    ?? 'en'
  const localizedPath = withLanguagePath(url.pathname, destinationLanguage)
  if (url.pathname !== localizedPath) {
    url.pathname = localizedPath
    changed = true
  }
  if (url.searchParams.has('lang')) {
    url.searchParams.delete('lang')
    changed = true
  }

  if (isAbsolute && ARENA_HOSTS.has(url.hostname) && context.arenaOrigin) {
    const arenaOrigin = new URL(context.arenaOrigin)
    if (url.origin !== arenaOrigin.origin) {
      url.host = arenaOrigin.host
      url.protocol = arenaOrigin.protocol
      changed = true
    }
  }

  if (!changed)
    return href
  return isRootRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
}

export function localizeFaqAnswerHtml(
  html: string,
  context: ContentLinkContext,
): string {
  return html.replaceAll(
    /(<a\b[^>]+?\bhref\s*=\s*)(["'])(.*?)\2/gi,
    (_match, before: string, quote: string, encodedHref: string) => {
      const href = encodedHref
        .replaceAll('&amp;', '&')
        .replaceAll('&#38;', '&')
        .replaceAll('&#x26;', '&')
      const localized = localizeContentHref(href, context) ?? href
      return `${before}${quote}${localized.replaceAll('&', '&amp;')}${quote}`
    },
  )
}

const HTML_LANGUAGE: Record<LanguageCode, string> = {
  en: 'en',
  zh: 'zh-Hans',
  tw: 'zh-Hant',
  ja: 'ja',
  ko: 'ko',
  ar: 'ar',
  es: 'es',
  fr: 'fr',
}

export function contentLanguageTag(language: LanguageCode): string {
  return HTML_LANGUAGE[language]
}

export interface ContentPageHead {
  title: string
  description: string
  htmlLanguage: string
  interfaceLanguage: LanguageCode
  robots: 'index,follow' | 'noindex,follow' | 'noindex,nofollow'
  canonicalUrl?: string
  alternates?: ReadonlyArray<{ hreflang: string, href: string }>
  imageUrl?: string
  pageType?: 'article' | 'website'
  structuredData?: Record<string, unknown>
  structuredDataId?: 'blog-structured-data' | 'blog-list-structured-data'
}

export function applyContentPageHead(head: ContentPageHead): () => void {
  const ownedElements: Element[] = []
  const production = window.location.hostname === 'mentiss.ai'
    || window.location.hostname === 'www.mentiss.ai'
  const robots = production ? head.robots : 'noindex,nofollow'
  const setMeta = (
    attribute: 'name' | 'property',
    key: string,
    value: string | undefined,
  ) => {
    document.head.querySelectorAll(`meta[${attribute}="${key}"]`)
      .forEach(element => element.remove())
    if (value === undefined)
      return

    const meta = document.createElement('meta')
    meta.setAttribute(attribute, key)
    meta.content = value
    document.head.append(meta)
    ownedElements.push(meta)
  }

  document.title = head.title
  document.documentElement.lang = head.htmlLanguage
  document.documentElement.dir = head.htmlLanguage === 'ar' ? 'rtl' : 'ltr'

  setMeta('name', 'description', head.description)
  setMeta('name', 'robots', robots)
  setMeta('name', 'keywords', undefined)
  setMeta('property', 'og:type', head.pageType ?? 'website')
  setMeta('property', 'og:title', head.title)
  setMeta('property', 'og:description', head.description)
  setMeta('property', 'og:url', head.canonicalUrl)
  setMeta('property', 'og:image', head.imageUrl)
  setMeta('property', 'og:image:width', undefined)
  setMeta('property', 'og:image:height', undefined)
  setMeta('name', 'twitter:card', 'summary_large_image')
  setMeta('name', 'twitter:title', head.title)
  setMeta('name', 'twitter:description', head.description)
  setMeta('name', 'twitter:url', head.canonicalUrl)
  setMeta('name', 'twitter:image', head.imageUrl)
  setMeta('name', 'twitter:image:width', undefined)
  setMeta('name', 'twitter:image:height', undefined)

  document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]')
    .forEach(element => element.remove())
  if (head.canonicalUrl) {
    const canonical = document.createElement('link')
    canonical.rel = 'canonical'
    canonical.href = head.canonicalUrl
    document.head.append(canonical)
    ownedElements.push(canonical)
  }
  for (const alternate of head.alternates ?? []) {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = alternate.hreflang
    link.href = alternate.href
    document.head.append(link)
    ownedElements.push(link)
  }

  const defaultStructuredData = document.head.querySelector('#site-default-structured-data')
  const defaultStructuredDataNext = defaultStructuredData?.nextSibling ?? null
  defaultStructuredData?.remove()
  document.head.querySelectorAll('#blog-structured-data, #blog-list-structured-data')
    .forEach(element => element.remove())
  if (head.structuredData && head.structuredDataId) {
    const script = document.createElement('script')
    script.id = head.structuredDataId
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(head.structuredData)
    document.head.append(script)
    ownedElements.push(script)
  }

  return () => {
    ownedElements.forEach(element => element.remove())
    if (defaultStructuredData) {
      const next = defaultStructuredDataNext?.parentNode === document.head
        ? defaultStructuredDataNext
        : null
      document.head.insertBefore(defaultStructuredData, next)
    }
    document.title = 'Mentiss | AI Werewolf, Mafia & Board Game Benchmark for LLMs'
    document.documentElement.lang = contentLanguageTag(head.interfaceLanguage)
    document.documentElement.dir = head.interfaceLanguage === 'ar' ? 'rtl' : 'ltr'
  }
}
